import React from "react";
import { Navigate } from "react-router-dom";
import {
  hasModuleAccess,
  hasMinimumRole,
  hasResourcePermission,
} from "utils/permissions.js";
import AccessDenied from "components/AccessDenied.js";

/**
 * ProtectedRoute component to control access to specific routes
 */
function ProtectedRoute({
  children,
  module = null,
  minimumRole = null,
  allowedRoles = null,
  resource = null,
  permission = null,
  redirectTo = "/admin/home",
  showAccessDenied = true,
}) {
  const userData = React.useMemo(() => {
    const storedUser = localStorage.getItem("user");
    return storedUser ? JSON.parse(storedUser) : null;
  }, []);

  const clientModules = React.useMemo(() => {
    const storedModules = localStorage.getItem("clientModules");
    return storedModules ? JSON.parse(storedModules) : [];
  }, []);

  // Not logged in
  if (!userData) {
    return <Navigate to="/auth/login" replace />;
  }

  // Role gate
  if (allowedRoles && !allowedRoles.includes(userData.user_role)) {
    return <Navigate to={redirectTo} replace />;
  }

  // Module-specific logic
  if (module === "HRMS" || module === "HUCS") {
    if (userData.user_role === "GLOBAL") {
      return children;
    }

    if (!hasModuleAccess(userData, clientModules, module)) {
      if (showAccessDenied) {
        return (
          <AccessDenied
            title="Module Access Denied"
            message={`Your organization does not have access to the ${module} module.`}
          />
        );
      }
      return <Navigate to={redirectTo} replace />;
    }
  }

  // Resource/page permission gate
  if (resource && permission) {
    if (!hasResourcePermission(userData, resource, permission)) {
      if (showAccessDenied) {
        return (
          <AccessDenied
            title="Insufficient Permissions"
            message="You do not have permission to access this page."
          />
        );
      }

      return <Navigate to={redirectTo} replace />;
    }
  }

  // Legacy minimumRole support
  if (minimumRole && !hasMinimumRole(userData, minimumRole)) {
    if (showAccessDenied) {
      return (
        <AccessDenied
          title="Insufficient Permissions"
          message={`This module requires ${minimumRole} role or higher.`}
        />
      );
    }
    return <Navigate to={redirectTo} replace />;
  }

  return children;
}
export default ProtectedRoute;
